// Unified Backup, Export, and Restore Manager
// Provides anti-data-loss protection across Cloud Run container resets, device changes, and browser restarts.

import { getStoredRawArtwork, saveTitleArtwork } from './titleArtworkStorage';
import { getWordOverridesMap, importWordOverridesBatch } from './wordOverridesStorage';
import { getAllLocalCustomWords, saveCustomWord } from './customVocabularyStorage';
import {
  getAllRecordedAudiosDataUrls,
  restoreRecordedAudiosBatch,
  forceAutoHealRecordingsToServer,
} from './audioStorage';
import { getActivePlayerId, getAllProfiles } from './saveManager';
import { LEVEL_DATA } from '../data/gameData';
import { DAILY_DIALOGUES_2026 } from '../data/dialoguesData';
import { SMS_DISCUSSIONS } from '../data/smsDiscussionsData';

export interface SystemBackupData {
  appName: string;
  version: string;
  exportDate: string;
  titleArtwork?: {
    dataUrl: string;
  } | null;
  wordOverrides: Record<string, { kab: string; fr: string; icon?: string }>;
  customVocabulary: Array<{
    id?: string;
    kab: string;
    fr: string;
    type?: string;
    icon?: string;
  }>;
  // Contes traditionnels kabyles (Parchemins)
  contes?: Array<{
    levelId: number;
    sanctuaryName: string;
    title: string;
    content: string;
  }>;
  // Dialogues du quotidien
  dialogues?: Array<{
    id: string;
    title: string;
    kabyleTitle: string;
    frenchTitle: string;
    location: string;
    desc: string;
    year?: string;
    scroll: {
      title: string;
      content: string;
    };
  }>;
  // Discussions SMS
  smsDiscussions?: Array<{
    id: string;
    title: string;
    contactName: string;
    contactRole: string;
    avatar: string;
    themeTag: string;
    contextDesc: string;
    messagesCount: number;
    messages: Array<{
      id: string;
      sender: 'contact' | 'player';
      kab: string;
      fr: string;
      time: string;
    }>;
  }>;
  stats?: {
    totalWordsCount: number;
    totalContesCount: number;
    totalDialoguesCount: number;
    totalSmsCount: number;
    totalRecordedAudiosCount: number;
  };
  recordedAudiosCount?: number;
  recordedAudios?: Record<string, string>; // Complete custom voice recordings (word -> base64 dataUrl)
}

/**
 * Generate and download a complete JSON backup file (including voice recordings, contes, dialogues, sms)
 */
export async function exportFullSystemBackup(): Promise<{ success: boolean; filename?: string; error?: string }> {
  try {
    const rawArtwork = await getStoredRawArtwork();
    const wordOverrides = getWordOverridesMap();
    const customVocabulary = getAllLocalCustomWords();
    const recordedAudios = await getAllRecordedAudiosDataUrls();

    const contesExport = LEVEL_DATA.filter((lvl) => lvl.scroll).map((lvl) => ({
      levelId: lvl.id,
      sanctuaryName: lvl.name,
      title: lvl.scroll!.title,
      content: lvl.scroll!.content,
    }));

    const dialoguesExport = DAILY_DIALOGUES_2026.map((d) => ({
      id: d.id,
      title: d.title,
      kabyleTitle: d.kabyleTitle,
      frenchTitle: d.frenchTitle,
      location: d.location,
      desc: d.desc,
      year: d.year,
      scroll: {
        title: d.scroll.title,
        content: d.scroll.content,
      },
    }));

    const smsExport = SMS_DISCUSSIONS.map((sms) => ({
      id: sms.id,
      title: sms.title,
      contactName: sms.contactName,
      contactRole: sms.contactRole,
      avatar: sms.avatar,
      themeTag: sms.themeTag,
      contextDesc: sms.contextDesc,
      messagesCount: sms.messages.length,
      messages: sms.messages,
    }));

    const backupData: SystemBackupData = {
      appName: 'Tamazight - Amawal Kabyle Game',
      version: '2.2.0',
      exportDate: new Date().toISOString(),
      titleArtwork: rawArtwork ? { dataUrl: rawArtwork } : null,
      stats: {
        totalWordsCount: Object.keys(wordOverrides).length + customVocabulary.length,
        totalContesCount: contesExport.length,
        totalDialoguesCount: dialoguesExport.length,
        totalSmsCount: smsExport.length,
        totalRecordedAudiosCount: Object.keys(recordedAudios).length,
      },
      wordOverrides,
      customVocabulary: customVocabulary.map((w) => ({
        id: w.id,
        kab: w.kab,
        fr: w.fr,
        type: w.type || 'Personnalisé',
        icon: w.icon || '📝',
      })),
      contes: contesExport,
      dialogues: dialoguesExport,
      smsDiscussions: smsExport,
      recordedAudiosCount: Object.keys(recordedAudios).length,
      recordedAudios,
    };

    const jsonString = JSON.stringify(backupData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = `${now.getHours()}h${now.getMinutes()}m`;
    const filename = `sauvegarde-amawal-kabyle_${dateStr}_${timeStr}.json`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (err: any) {
    console.error('Error generating backup:', err);
    return { success: false, error: err.message || 'Erreur lors de la génération de la sauvegarde' };
  }
}

/**
 * Generate full backup as a JSON string for clipboard transfer
 */
export async function getBackupDataString(): Promise<{
  success: boolean;
  jsonString?: string;
  count?: number;
  error?: string;
}> {
  try {
    const rawArtwork = await getStoredRawArtwork();
    const wordOverrides = getWordOverridesMap();
    const customVocabulary = getAllLocalCustomWords();
    const recordedAudios = await getAllRecordedAudiosDataUrls();

    const contesExport = LEVEL_DATA.filter((lvl) => lvl.scroll).map((lvl) => ({
      levelId: lvl.id,
      sanctuaryName: lvl.name,
      title: lvl.scroll!.title,
      content: lvl.scroll!.content,
    }));

    const dialoguesExport = DAILY_DIALOGUES_2026.map((d) => ({
      id: d.id,
      title: d.title,
      kabyleTitle: d.kabyleTitle,
      frenchTitle: d.frenchTitle,
      location: d.location,
      desc: d.desc,
      year: d.year,
      scroll: {
        title: d.scroll.title,
        content: d.scroll.content,
      },
    }));

    const smsExport = SMS_DISCUSSIONS.map((sms) => ({
      id: sms.id,
      title: sms.title,
      contactName: sms.contactName,
      contactRole: sms.contactRole,
      avatar: sms.avatar,
      themeTag: sms.themeTag,
      contextDesc: sms.contextDesc,
      messagesCount: sms.messages.length,
      messages: sms.messages,
    }));

    const backupData: SystemBackupData = {
      appName: 'Tamazight - Amawal Kabyle Game',
      version: '2.2.0',
      exportDate: new Date().toISOString(),
      titleArtwork: rawArtwork ? { dataUrl: rawArtwork } : null,
      stats: {
        totalWordsCount: Object.keys(wordOverrides).length + customVocabulary.length,
        totalContesCount: contesExport.length,
        totalDialoguesCount: dialoguesExport.length,
        totalSmsCount: smsExport.length,
        totalRecordedAudiosCount: Object.keys(recordedAudios).length,
      },
      wordOverrides,
      customVocabulary: customVocabulary.map((w) => ({
        id: w.id,
        kab: w.kab,
        fr: w.fr,
        type: w.type || 'Personnalisé',
        icon: w.icon || '📝',
      })),
      contes: contesExport,
      dialogues: dialoguesExport,
      smsDiscussions: smsExport,
      recordedAudiosCount: Object.keys(recordedAudios).length,
      recordedAudios,
    };

    const jsonString = JSON.stringify(backupData);
    return {
      success: true,
      jsonString,
      count: Object.keys(recordedAudios).length,
    };
  } catch (err: any) {
    console.error('Error stringifying backup:', err);
    return { success: false, error: err.message || 'Erreur lors de la préparation de la sauvegarde' };
  }
}

/**
 * Import and restore a JSON backup file into local stores and the server backend
 */
export async function importFullSystemBackup(jsonString: string): Promise<{
  success: boolean;
  artworkRestored: boolean;
  wordsRestoredCount: number;
  customWordsRestoredCount: number;
  recordedAudiosRestoredCount: number;
  error?: string;
}> {
  try {
    const parsed = JSON.parse(jsonString) as Partial<SystemBackupData>;

    let artworkRestored = false;
    let wordsRestoredCount = 0;
    let customWordsRestoredCount = 0;
    let recordedAudiosRestoredCount = 0;

    // 1. Restore Title Artwork if present
    if (parsed.titleArtwork && parsed.titleArtwork.dataUrl && parsed.titleArtwork.dataUrl.startsWith('data:image')) {
      const artRes = await saveTitleArtwork(parsed.titleArtwork.dataUrl);
      if (artRes.success) {
        artworkRestored = true;
      }
    }

    // 2. Restore Word Overrides (Modified words)
    if (parsed.wordOverrides && typeof parsed.wordOverrides === 'object') {
      const entries = Object.entries(parsed.wordOverrides);
      if (entries.length > 0) {
        await importWordOverridesBatch(parsed.wordOverrides);
        wordsRestoredCount = entries.length;
      }
    }

    // 3. Restore Custom Added Vocabulary
    if (Array.isArray(parsed.customVocabulary) && parsed.customVocabulary.length > 0) {
      const activePlayerId = getActivePlayerId();
      for (const item of parsed.customVocabulary) {
        if (item.kab && item.fr) {
          saveCustomWord(
            {
              id: item.id,
              kab: item.kab,
              fr: item.fr,
              icon: item.icon || '📝',
              type: 'Personnalisé',
            },
            activePlayerId
          );
          customWordsRestoredCount++;
        }
      }
    }

    // 4. Restore Custom Audio Recordings (Permanent Voice Bank)
    if (parsed.recordedAudios && typeof parsed.recordedAudios === 'object') {
      recordedAudiosRestoredCount = await restoreRecordedAudiosBatch(parsed.recordedAudios);
    }

    return {
      success: true,
      artworkRestored,
      wordsRestoredCount,
      customWordsRestoredCount,
      recordedAudiosRestoredCount,
    };
  } catch (err: any) {
    console.error('Error restoring backup file:', err);
    return {
      success: false,
      artworkRestored: false,
      wordsRestoredCount: 0,
      customWordsRestoredCount: 0,
      recordedAudiosRestoredCount: 0,
      error: 'Le fichier sélectionné n\'est pas un fichier JSON de sauvegarde valide.',
    };
  }
}

/**
 * Force Auto-Heal: Re-push all client local data (Artwork, Words, Custom Vocab, and Voice Recordings) to newly restarted server container
 */
export async function forceAutoHealAllToServer(): Promise<{
  success: boolean;
  artworkHealed: boolean;
  wordsHealedCount: number;
  customWordsHealedCount: number;
  recordedAudiosHealedCount: number;
}> {
  let artworkHealed = false;
  let wordsHealedCount = 0;
  let customWordsHealedCount = 0;
  let recordedAudiosHealedCount = 0;

  try {
    // 1. Title Artwork
    const localRawArtwork = await getStoredRawArtwork();
    if (localRawArtwork) {
      const res = await fetch('/api/title-artwork', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageDataUrl: localRawArtwork }),
      });
      if (res.ok) {
        artworkHealed = true;
      }
    }

    // 2. Word Overrides
    const overrides = getWordOverridesMap();
    const count = Object.keys(overrides).length;
    if (count > 0) {
      const res = await fetch('/api/word-overrides', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch: overrides }),
      });
      if (res.ok) {
        wordsHealedCount = count;
      }
    }

    // 3. Custom Words for active player
    const activePlayerId = getActivePlayerId();
    const customWords = getAllLocalCustomWords();
    if (customWords.length > 0) {
      const res = await fetch(`/api/player-vocabulary/${encodeURIComponent(activePlayerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: customWords }),
      });
      if (res.ok) {
        customWordsHealedCount = customWords.length;
      }
    }

    // 4. Custom Voice Recordings (Auto-Heal server audio folder on disk)
    const audioHeal = await forceAutoHealRecordingsToServer();
    if (audioHeal.success) {
      recordedAudiosHealedCount = audioHeal.count;
    }

    return {
      success: true,
      artworkHealed,
      wordsHealedCount,
      customWordsHealedCount,
      recordedAudiosHealedCount,
    };
  } catch (err) {
    console.error('Force auto-heal error:', err);
    return {
      success: false,
      artworkHealed,
      wordsHealedCount,
      customWordsHealedCount,
      recordedAudiosHealedCount,
    };
  }
}
